import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Task } from '../types/Task';
import { taskApi, aiApi } from '../services/api';
import { MessageCircle, Send } from 'lucide-react';
import './TaskAnalyticsChat.css';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  analysis?: any; // Keep this generic since it comes from OpenAI
}

const TaskAnalyticsChat: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load tasks function with useCallback to prevent infinite rerenders
  const loadTasks = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('🔍 TaskAnalyticsChat: Starting to load tasks...');
      
      // Check if user is authenticated
      const token = localStorage.getItem('auth_token');
      console.log('🔑 Auth token present:', !!token);
      
      const response = await taskApi.getTasks();
      console.log('📊 Tasks API response:', response);
      console.log('📋 Tasks data:', response.tasks);
      console.log('🔢 Number of tasks loaded:', response.tasks?.length || 0);
      
      setTasks(response.tasks || []);
      
      // Add welcome message
      if (!isInitialized) {
        const welcomeMessage: ChatMessage = {
          id: Date.now().toString(),
          type: 'assistant',
          content: `👋 Cześć! Jestem Twoim AI asystentem do analizy zadań.\n\nZaładowałem ${response.tasks?.length || 0} zadań z Twojej organizacji. Możesz zapytać mnie o:\n\n• "Co jest najczęstszym problemem?"\n• "Które zadania są zablokowane?"\n• "Kto ma najwięcej zadań?"\n• "Jakie są typowe opóźnienia?"\n• "Pokaż statystyki priorytetów"\n• "Które tagi są najpopularniejsze?"\n\nZacznijmy analizę! 🚀`,
          timestamp: new Date()
        };
        setMessages([welcomeMessage]);
        setIsInitialized(true);
      }
    } catch (error: any) {
      console.error('❌ Failed to load tasks:', error);
      console.error('❌ Error details:', error.response || error.message || error);
      
      let errorText = '❌ Nie udało się załadować zadań. ';
      
      if (error.response?.status === 401) {
        errorText += 'Musisz się zalogować.';
      } else if (error.response?.status === 403) {
        errorText += 'Brak dostępu do organizacji.';
      } else if (error.response?.status === 500) {
        errorText += 'Problem z serwerem.';
      } else {
        errorText += 'Sprawdź połączenie i spróbuj ponownie.';
      }
      
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: errorText,
        timestamp: new Date()
      };
      setMessages([errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized]); // Add isInitialized as dependency

  // Load tasks on component mount
  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Analyze with AI API
      console.log('🤖 Sending query to AI:', inputMessage);
      console.log('📊 Processing', tasks.length, 'tasks...');
      
      const response = await aiApi.analyzeTasks(inputMessage);

      // Simulate thinking time for better UX
      setTimeout(() => {
        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: response.response,
          timestamp: new Date(),
          analysis: response.analysis
        };

        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
      }, 800);

    } catch (error: any) {
      console.error('Error analyzing tasks:', error);
      
      let errorText = '❌ Przepraszam, wystąpił błąd podczas analizy danych. ';
      if (error.code === 'ECONNABORTED') {
        errorText += `Analiza ${tasks.length} zadań trwa dłużej niż oczekiwano. Spróbuj z mniejszą liczbą zadań lub zaczekaj chwilę i spróbuj ponownie.`;
      } else {
        errorText += 'Spróbuj ponownie.';
      }
      
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: errorText,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const suggestedQuestions = [
    "Co jest najczęstszym problemem?",
    "Które zadania są zablokowane?", 
    "Kto ma najwięcej zadań?",
    "Pokaż statystyki priorytetów",
    "Jakie są opóźnienia?",
    "Które tagi są najpopularniejsze?"
  ];

  return (
    <div className="task-analytics-chat">
      <div className="chat-header">
        <div className="header-title">
          <MessageCircle size={20} />
          <h3>AI Task Analyst</h3>
        </div>
        <div className="task-counter">
          {tasks.length} zadań załadowanych
        </div>
      </div>

      <div className="chat-messages">
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type}`}>
            <div className="message-content">
              <div className="message-text">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {message.content}
                </ReactMarkdown>
              </div>
              <div className="message-timestamp">
                {message.timestamp.toLocaleTimeString('pl-PL', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message assistant">
            <div className="message-content">
              <div className="thinking-indicator">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.8 }}>
                🤖 Analizuję {tasks.length} zadań z pomocą AI...
                <br />
                <span style={{ fontSize: '0.75rem' }}>
                  To może potrwać do 60 sekund dla dużej liczby zadań
                </span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {messages.length === 1 && (
        <div className="suggested-questions">
          <div className="suggestions-header">💡 Przykładowe pytania:</div>
          <div className="suggestions-grid">
            {suggestedQuestions.map((question, index) => (
              <button
                key={index}
                className="suggestion-btn"
                onClick={() => setInputMessage(question)}
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="chat-input">
        <div className="input-container">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Zapytaj o zadania, problemy, statystyki..."
            rows={1}
            disabled={isLoading}
          />
          <button
            className="send-btn"
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskAnalyticsChat; 